import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/DropdownMenu";
import { ChevronDown } from "lucide-react";
import { Fragment } from "react";
import type { WorkflowStatus, WorkflowSummary } from "../types";
import { StatusTag } from "./StatusTag";

type Props = {
  workflows: WorkflowSummary[];
  selected: WorkflowSummary;
  onSelect: (name: string) => void;
};

const groupsOf = (workflows: WorkflowSummary[]): [string, WorkflowSummary[]][] => {
  const groups = new Map<string, WorkflowSummary[]>();
  for (const workflow of workflows) {
    const group = workflow.group ?? "Other workflows";
    groups.set(group, [...(groups.get(group) ?? []), workflow]);
  }
  return [...groups];
};

const statusTag = (status: WorkflowStatus) =>
  status === "in-use" ? null : <StatusTag status={status} />;

/** A drop-down list of the workflows, in their groups. */
export const WorkflowPicker = ({ workflows, selected, onSelect }: Props) => (
  <div className="flex flex-wrap items-center gap-3">
    <span id="workflow-picker-label" className="text-sm font-medium text-zinc-700">
      Workflow
    </span>
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-labelledby="workflow-picker-label workflow-picker-value"
          className="inline-flex min-w-72 items-center justify-between gap-3 rounded-lg border border-zinc-300 bg-white px-3 py-2 text-left text-sm font-medium text-zinc-900 shadow-sm transition-colors hover:border-zinc-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-400 data-[state=open]:border-zinc-500"
        >
          <span id="workflow-picker-value" className="flex items-center gap-2">
            {selected.label}
            {statusTag(selected.status)}
          </span>
          <ChevronDown className="h-4 w-4 text-zinc-500" aria-hidden />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="max-h-[var(--radix-dropdown-menu-content-available-height)] w-96 overflow-y-auto"
      >
        <DropdownMenuRadioGroup value={selected.name} onValueChange={onSelect}>
          {groupsOf(workflows).map(([group, members], index) => (
            <Fragment key={group}>
              {index > 0 && <DropdownMenuSeparator />}
              <DropdownMenuLabel>{group}</DropdownMenuLabel>
              {members.map((workflow) => (
                <DropdownMenuRadioItem key={workflow.name} value={workflow.name}>
                  <span className="flex flex-col gap-0.5">
                    <span className="flex items-center gap-2 font-medium text-zinc-900">
                      {workflow.label}
                      {statusTag(workflow.status)}
                    </span>
                    <span className="line-clamp-2 text-xs leading-5 text-zinc-500">
                      {workflow.description}
                    </span>
                  </span>
                </DropdownMenuRadioItem>
              ))}
            </Fragment>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  </div>
);
